import {
  SEED_MARKETS,
  marketsForScope,
  type AttributionStatus,
  type Click,
  type Currency,
  type Market,
  type Merchant,
  type Transaction,
  type TransactionStatus,
  type User,
} from '../dataProvider/types';
import { ACTIONABLE_STATES, CANDIDATE_WINDOW_DAYS } from './attributionState';

/**
 * The Manual attribution query/aggregation module - the ONE place orphan
 * transactions are shaped for the queue. Same seam idiom as referrals /
 * subscriptions: v1 aggregates client-side from the mock provider, every selector
 * lives here, and the late real-data migration swaps this module for a server-side
 * endpoint without the surface changing.
 *
 * An ORPHAN is a transaction that arrived with no confidently-known member (userId
 * null), carrying an attributionStatus review state. Normal transactions are
 * attributed by construction and never appear here. A worked orphan (credited or
 * rejected through the queue) carries a BASIS (matchedClickId or attributionNote),
 * which is how a credited orphan is told apart from a normal attributed transaction.
 *
 * CANDIDATES are a DISPLAY aid, not enforcement: for an actionable orphan the module
 * surfaces clicks on the same merchant and market within a time window, each
 * carrying the member it would credit to. Ops pick one (or credit with a
 * justification, or reject). The module never auto-credits and never ranks-and-picks.
 *
 * BANDING is by the orphan's market, which comes from the MERCHANT side
 * (merchantId / merchantRegionId), so an orphan bands before it has a member.
 */

const DAY = 86_400_000;

export interface CandidateClick {
  clickId: number;
  /** The member this candidate would credit the orphan to. */
  userId: number;
  memberName: string;
  occurredAt: string;
}

export interface OrphanRow {
  /** The transaction id. */
  id: number;
  market: Market;
  merchantId: number;
  storeName: string;
  grossAmount: number;
  currency: Currency;
  occurredAt: string;
  /** The settlement status (pending / confirmed / ...), ORTHOGONAL to attribution. */
  status: TransactionStatus;
  attributionStatus: AttributionStatus;
  userId: number | null;
  /** The credited member's name, when attributed. */
  memberName: string | null;
  matchedClickId: number | null;
  attributionNote: string | null;
  /** Candidate clicks to match on (actionable orphans only). */
  candidates: CandidateClick[];
  /** Attributed but not yet confirmed: a member is linked but no value has moved. */
  pendingConfirmation: boolean;
}

export type AttrCounts = Record<AttributionStatus, number>;

export interface AttributionBand {
  market: Market;
  /** Orphans still to work: unattributed + needs_review. */
  actionable: OrphanRow[];
  /** Worked orphans: credited (attributed with a basis) + rejected. */
  history: OrphanRow[];
  counts: AttrCounts;
}

export interface AttributionView {
  bands: AttributionBand[];
}

export interface AttributionInput {
  transactions: Transaction[];
  clicks: Click[];
  users: User[];
  merchants: Merchant[];
}

export interface AttributionOptions {
  scope: string;
}

/** The market bands a scope renders, kept to operating markets. */
export const bandsForScope = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

const emptyCounts = (): AttrCounts => ({
  unattributed: 0,
  needs_review: 0,
  attributed: 0,
  rejected: 0,
});

/** True for an orphan transaction (a member was not known at ingestion). A normal
 *  transaction is attributed with no basis, so it is excluded everywhere. */
const isOrphan = (t: Transaction): boolean => {
  const s = t.attributionStatus;
  if (s == null) return false;
  if (s === 'unattributed' || s === 'needs_review' || s === 'rejected') {
    return true;
  }
  // attributed: an orphan only if it was worked through the queue (carries a basis).
  return t.matchedClickId != null || t.attributionNote != null;
};

const isActionable = (t: Transaction): boolean =>
  t.attributionStatus != null &&
  ACTIONABLE_STATES.includes(t.attributionStatus);

export function buildAttributionView(
  input: AttributionInput,
  opts: AttributionOptions,
): AttributionView {
  const usersById = new Map(input.users.map((u) => [u.id, u]));
  const merchantsById = new Map(input.merchants.map((m) => [m.id, m]));

  const candidatesFor = (orphan: Transaction): CandidateClick[] => {
    const at = Date.parse(orphan.occurredAt);
    return input.clicks
      .filter(
        (c) =>
          c.merchantId === orphan.merchantId &&
          c.market === orphan.market &&
          Math.abs(Date.parse(c.occurredAt) - at) <=
            CANDIDATE_WINDOW_DAYS * DAY,
      )
      .map((c) => ({
        clickId: c.id,
        userId: c.userId,
        memberName: usersById.get(c.userId)?.name ?? `Member ${c.userId}`,
        occurredAt: c.occurredAt,
      }))
      .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  };

  const toRow = (t: Transaction): OrphanRow => {
    const actionable = isActionable(t);
    const memberName =
      t.userId != null ? (usersById.get(t.userId)?.name ?? null) : null;
    return {
      id: t.id,
      market: t.market,
      merchantId: t.merchantId,
      storeName: merchantsById.get(t.merchantId)?.name ?? '',
      grossAmount: t.grossAmount,
      currency: t.currency,
      occurredAt: t.occurredAt,
      status: t.status,
      attributionStatus: t.attributionStatus as AttributionStatus,
      userId: t.userId,
      memberName,
      matchedClickId: t.matchedClickId ?? null,
      attributionNote: t.attributionNote ?? null,
      candidates: actionable ? candidatesFor(t) : [],
      pendingConfirmation:
        t.attributionStatus === 'attributed' && t.status !== 'confirmed',
    };
  };

  const orphans = input.transactions.filter(isOrphan);

  const bands = bandsForScope(opts.scope).map((market) => {
    const rows = orphans
      .filter((t) => t.market === market)
      .map(toRow)
      .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
    const counts = emptyCounts();
    for (const r of rows) counts[r.attributionStatus] += 1;
    return {
      market,
      actionable: rows.filter((r) =>
        ACTIONABLE_STATES.includes(r.attributionStatus),
      ),
      history: rows.filter(
        (r) => !ACTIONABLE_STATES.includes(r.attributionStatus),
      ),
      counts,
    };
  });

  return { bands };
}
